// skyhawks-peninsula-internal-web/src/components/Admin/StaffPerformanceTab.js

import React, { useState } from 'react';
import Select from 'react-select';
import { getTierSummary } from '../../api/tiers';
import Loader from '../common/Loader';
import CoachTierDetailModal from './CoachTierDetailModal';

const currentYear = new Date().getFullYear();
const yearOptions = [2024, 2025, 2026, 2027].map(y => ({ value: y, label: String(y) }));
const seasonOptions = ['Spring', 'Summer', 'Fall', 'Winter'].map(s => ({ value: s.toLowerCase(), label: s }));

const TIER_DISPLAY = {
  'all-star': { label: 'All-Star', className: 'tier-badge tier-all-star' },
  'varsity': { label: 'Varsity', className: 'tier-badge tier-varsity' },
  'jv': { label: 'JV', className: 'tier-badge tier-jv' },
};

function StaffPerformanceTab() {
  const [yearFilter, setYearFilter] = useState({ value: currentYear, label: String(currentYear) });
  const [seasonFilter, setSeasonFilter] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);

  const handleLoad = async () => {
    if (!seasonFilter) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getTierSummary(yearFilter.value, seasonFilter.value);
      if (response?.error) {
        setError('Failed to load tier data.');
        setData([]);
      } else {
        setData(response?.data || []);
      }
    } catch (err) {
      setError('Failed to load tier data.');
      setData([]);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  const formatBonus = (amount) => {
    if (amount == null || amount === 0) return '—';
    return `$${amount.toFixed(2)}`;
  };

  const formatTier = (tier) => {
    const display = TIER_DISPLAY[tier] || { label: tier, className: 'tier-badge' };
    return <span className={display.className}>{display.label}</span>;
  };

  const formatProgress = (row) => {
    const parts = [];
    parts.push(`${row.programsCompleted} programs`);
    parts.push(`${row.positiveSignalsCount} signals`);
    if (row.complaintsCount > 0) {
      parts.push(`${row.complaintsCount} complaint${row.complaintsCount > 1 ? 's' : ''}`);
    }
    return parts.join(' · ');
  };

  const formatEndorsement = (level) => {
    if (!level) return '—';
    const labels = {
      'do-not-recommend': 'Do Not Recommend',
      'recommend': 'Recommend',
      'strongly-recommend': 'Strongly Recommend',
    };
    return labels[level] || level;
  };

  return (
    <div className="performance-tab">
      <div className="filters-bar">
        <div className="filter-group">
          <label>Year</label>
          <Select
            value={yearFilter}
            onChange={setYearFilter}
            options={yearOptions}
            placeholder="Year"
            className="filter-select"
            classNamePrefix="filter"
          />
        </div>
        <div className="filter-group">
          <label>Season</label>
          <Select
            value={seasonFilter}
            onChange={setSeasonFilter}
            options={seasonOptions}
            isClearable
            placeholder="Select season"
            className="filter-select"
            classNamePrefix="filter"
          />
        </div>
        <div className="filter-group filter-group--action">
          <label>&nbsp;</label>
          <button
            className="load-btn"
            onClick={handleLoad}
            disabled={!seasonFilter || loading}
          >
            {loading ? 'Loading...' : 'Load'}
          </button>
        </div>
      </div>

      {loading && <Loader size={60} />}

      {error && !loading && (
        <div className="performance-error">{error}</div>
      )}

      {!loading && hasLoaded && !error && (
        <>
          {data.length === 0 ? (
            <div className="staff-count">No tier data found for this period.</div>
          ) : (
            <>
              <div className="staff-count">Showing {data.length} coach{data.length !== 1 ? 'es' : ''}</div>
              <div className="staff-table-container">
                <table className="staff-table">
                  <thead>
                    <tr>
                      <th>Coach</th>
                      <th>Tier</th>
                      <th>Progress</th>
                      <th>Review</th>
                      <th>Bonus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...data].sort((a, b) => b.programsCompleted - a.programsCompleted).map((row) => (
                      <tr
                        key={row.coachId}
                        onClick={() => setSelectedCoach(row)}
                        className="clickable-row"
                      >
                        <td>{row.coachName || row.coachId}</td>
                        <td>{formatTier(row.tier)}</td>
                        <td>{formatProgress(row)}</td>
                        <td>{formatEndorsement(row.endorsementLevel)}</td>
                        <td>{formatBonus(row.bonusAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {!loading && !hasLoaded && (
        <div className="staff-count">Select a year and season, then click Load.</div>
      )}

      {selectedCoach && (
        <CoachTierDetailModal
          coachId={selectedCoach.coachId}
          coachName={selectedCoach.coachName}
          year={yearFilter.value}
          season={seasonFilter.value}
          bonusAmount={selectedCoach.bonusAmount}
          onClose={() => setSelectedCoach(null)}
          onDataChange={handleLoad}
        />
      )}
    </div>
  );
}

export default StaffPerformanceTab;
